import { z } from 'zod';
import {
  getQuizStorageKeys,
  MINI_QUIZ_STORAGE_NAMESPACE,
} from '../../defs/localStorage';
import {
  createSerializedNativationSchema,
  deserializeNavState,
} from '../schema';
import { miniQuizResumeRoutesSchema } from './routes';

export const MINI_QUIZ_RESUME_STORAGE_KEY = 'mini-quiz.resume';

const miniQuizExitKindSchema = z.enum(['close', 'save-and-exit']);

export type MiniQuizExitKind = z.infer<typeof miniQuizExitKindSchema>;

const miniQuizResumeStateSchema = z.object({
  visitedPathname: miniQuizResumeRoutesSchema,
  exitPathname: miniQuizResumeRoutesSchema.optional(),
  exitKind: miniQuizExitKindSchema.optional(),
  lastCommittedPathname: miniQuizResumeRoutesSchema.optional(),
  timestamp: z.coerce.date(),
});

export type MiniQuizResumeState = z.infer<typeof miniQuizResumeStateSchema>;

const RESUME_TTL_HOURS = 24;

function readRawResume(): unknown {
  try {
    const raw = window.localStorage.getItem(MINI_QUIZ_RESUME_STORAGE_KEY);
    if (raw === null) {
      return undefined;
    }

    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function migrateFromLegacyNav(): MiniQuizResumeState | undefined {
  const navKey = getQuizStorageKeys(MINI_QUIZ_STORAGE_NAMESPACE).nav;
  const raw = window.localStorage.getItem(navKey);
  if (raw === null) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }

  const legacy = deserializeNavState(
    parsed,
    createSerializedNativationSchema(miniQuizResumeRoutesSchema)
  );
  if (legacy === undefined) {
    return undefined;
  }

  return {
    visitedPathname: legacy.pathname,
    timestamp: legacy.timestamp,
  };
}

function isResumeExpired(timestamp: Date): boolean {
  const now = new Date();
  const msDiff = Math.abs(timestamp.getTime() - now.getTime());
  const hrsDiff = msDiff / (60 * 60 * 1000);
  return hrsDiff > RESUME_TTL_HOURS;
}

export function readMiniQuizResumeState(): MiniQuizResumeState | undefined {
  const parsed = readRawResume();
  if (parsed !== undefined) {
    const validated = miniQuizResumeStateSchema.safeParse(parsed);
    if (validated.success) {
      if (isResumeExpired(validated.data.timestamp)) {
        return undefined;
      }
      return validated.data;
    }
  }

  const migrated = migrateFromLegacyNav();
  if (migrated === undefined) {
    return undefined;
  }

  if (isResumeExpired(migrated.timestamp)) {
    return undefined;
  }

  writeMiniQuizResumeState(migrated);
  return migrated;
}

export function writeMiniQuizResumeState(state: MiniQuizResumeState): void {
  try {
    window.localStorage.setItem(
      MINI_QUIZ_RESUME_STORAGE_KEY,
      JSON.stringify({
        ...state,
        timestamp: state.timestamp,
      })
    );
  } catch {
    // localStorage can throw in private mode; resume falls back to defaults.
  }
}

export function updateMiniQuizResumeState(
  updater: (current: MiniQuizResumeState | undefined) => MiniQuizResumeState
): void {
  const current = readMiniQuizResumeState();
  writeMiniQuizResumeState(updater(current));
}

export function clearMiniQuizResumeState(): void {
  try {
    window.localStorage.removeItem(MINI_QUIZ_RESUME_STORAGE_KEY);
  } catch {
    // Same private-mode swallow as writeMiniQuizResumeState.
  }
}

export function getValidExitPathname(
  state: MiniQuizResumeState | undefined
): string | undefined {
  if (state?.exitPathname === undefined) {
    return undefined;
  }

  if (isResumeExpired(state.timestamp)) {
    return undefined;
  }

  return state.exitPathname;
}
